import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, MessageSquare, Trash2 } from 'lucide-react';
import { expertApi } from '../../services/expertApi';
import type { ExpertConversation } from '../../types/expert';

interface ConversationListProps {
  activeId: number | null;
  onSelect: (id: number) => void;
  onNewChat: () => void;
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const ConversationList: React.FC<ConversationListProps> = ({
  activeId,
  onSelect,
  onNewChat,
}) => {
  const queryClient = useQueryClient();
  const { data: conversations = [], isLoading } = useQuery<ExpertConversation[]>({
    queryKey: ['expert-conversations'],
    queryFn: () => expertApi.getConversations({ limit: 50 }),
  });

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await expertApi.deleteConversation(id);
      queryClient.invalidateQueries({ queryKey: ['expert-conversations'] });
    } catch {
      // silently fail
    }
  };

  return (
    <div className="flex flex-col h-full">
      <button
        onClick={onNewChat}
        className="mx-3 mt-3 mb-2 flex items-center justify-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-medium rounded-lg transition-colors text-sm"
      >
        <Plus size={16} />
        New Chat
      </button>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-slate-800 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center text-slate-500 text-sm p-6">
            No conversations yet
          </div>
        ) : (
          <div className="space-y-0.5 p-2">
            {conversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => onSelect(conv.id)}
                className={`group flex items-start gap-2 px-3 py-2.5 cursor-pointer rounded-lg transition-colors ${
                  activeId === conv.id
                    ? 'bg-slate-800 border-l-2 border-teal-500'
                    : 'hover:bg-slate-800/50'
                }`}
              >
                <MessageSquare size={14} className="text-slate-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-200 truncate">
                    {conv.title || 'New conversation'}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-500">
                      {conv.message_count} msgs
                    </span>
                    <span className="text-xs text-slate-600">
                      {conv.updated_at ? timeAgo(conv.updated_at) : ''}
                    </span>
                  </div>
                </div>
                <button
                  onClick={e => handleDelete(e, conv.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-red-400 transition-all"
                  title="Delete conversation"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;
